import { getImageCode, getSMSCode } from "@/apis/common";
import { register } from "@/apis/user";
import { useConfigContext } from "@/app";
import { usePopup } from "@/components/Popup";
import {
  validateConfirmPassword,
  validateImageCode,
  validatePassword,
  validatePhoneNumber,
  validateSMSCode,
  validateUserName,
} from "@/libs";
import { StateActions, useCreateReducer } from "@/libs/reducer";
import { Toast } from "antd-mobile";
import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { insertDx } from "@/logic/index";

export interface PageState {
  mode?: number;
  verifyCode: string;
  verifyUniqid: string;
  // 表单数据
  username: string;
  password: string;
  confirmPassword: string;
  imageCode: string;
  dxToken: string;
  smsCode: string;
  phoneNumber: string;
  // 顶象验证
  verifyToken: string;
}

const pagestate = {
  mode: 0,
  verifyCode: "",
  verifyUniqid: "",
  username: "",
  password: "",
  confirmPassword: "",
  imageCode: "",
  dxToken: "",
  smsCode: "",
  phoneNumber: "",
  verifyToken: "",
};

type ActionTypes =
  | "set_code_base64_img"
  | "user_info_fill_out"
  | "update_username"
  | "update_password"
  | "update_confirmPassword"
  | "update_imageCode"
  | "update_mode"
  | "update_phoneNumber"
  | "update_smsCode"
  | "register_failed"
  | "set_dx_token";

const stateReducer = (
  state: PageState,
  action: StateActions<PageState, ActionTypes>,
) => {
  switch (action.type) {
    case "set_code_base64_img":
      return Object.assign({}, state, {
        verifyCode: action?.verifyCode,
        verifyUniqid: action?.verifyUniqid,
      });
    case "user_info_fill_out":
      return Object.assign({}, state, {
        username: action?.username,
        password: action?.password,
      });
    case "update_username":
      return Object.assign({}, state, {
        username: action?.username,
      });
    case "update_password":
      return Object.assign({}, state, {
        password: action?.password,
      });
    case "update_confirmPassword":
      return Object.assign({}, state, {
        confirmPassword: action?.confirmPassword,
      });
    case "update_imageCode":
      return Object.assign({}, state, {
        imageCode: action?.imageCode,
      });
    case "update_mode":
      return Object.assign({}, state, {
        mode: action?.mode,
      });
    case "update_phoneNumber":
      return Object.assign({}, state, {
        phoneNumber: action?.phoneNumber,
      });
    case "update_smsCode":
      return Object.assign({}, state, {
        smsCode: action?.smsCode,
      });
    case "register_failed":
      return Object.assign({}, state, {
        verifyToken: "",
        imageCode: "",
      });
    case "set_dx_token":
      return Object.assign({}, state, {
        dxToken: action?.dxToken,
      });
    default:
      return state;
  }
};

export const useSetup = () => {
  const navigate = useNavigate();
  const { config } = useConfigContext();
  const [showPopup] = usePopup();
  const [loading, setLoading] = useState<boolean>();
  const [dxObj, setDxObj] = useState<any>(undefined);

  const announce = (className: string, content: ReactNode) => {
    return showPopup({
      closeOnClick: true,
      className,
      content,
    });
  };

  const [state, dispatch] = useCreateReducer<PageState, ActionTypes>(
    stateReducer,
    pagestate,
    {
      mode: Number(config?.userRegisterType || 2),
    },
  );

  const isUserNameRegisterMode = state.mode === 1;
  const isPhoneNumberRegisterMode = state.mode === 2;

  const flushCodeImage = async () => {
    const response = await getImageCode({ codeType: "register" });
    if (response.ok) {
      dispatch({
        type: "set_code_base64_img",
        verifyCode: response.data?.verifyCode,
        verifyUniqid: response.data?.verifyUniqid,
      });
    } else {
      // TODO: 请求失败的处理
    }
  };

  const flushSMSCode = async () => {
    const isPhoneNumberValid = validatePhoneNumber(state.phoneNumber);
    if (isPhoneNumberValid) {
      Toast.show(isPhoneNumberValid);
      return Promise.resolve(false);
    }
    const response = await getSMSCode({
      phoneNumber: state.phoneNumber,
      codeType: "register",
    });
    Toast.show(response.msg);
    return response.ok;
  };

  const doRegister = async () => {
    setLoading(true);
    const response = await register({
      userName: isPhoneNumberRegisterMode ? state.phoneNumber : state.username,
      smsCode: state.smsCode,
      password: state.password,
      code: state.imageCode,
      verifyUniqid: state.verifyUniqid,
      verifyToken: state.dxToken,
    });

    if (response.ok) {
      navigate("/auth/login");
    } else {
      // TODO: 顶象验证 刷新
      flushCodeImage();
      console.log('dxObj==',dxObj);
      dxObj && dxObj.reload();
      dispatch({ type: "register_failed" });
    }
    setLoading(false);
    Toast.show(response.msg);
  };

  const usernameRegister = () => {
    const isUsernameValid = validateUserName(state.username);
    const isPasswordValid = validatePassword(state.password);
    const isConfirmPasswordValid = validateConfirmPassword(
      state.confirmPassword,
      state.password,
    );

    let isImageCodeValid = undefined;
    let isverifyTokenValid = undefined;
    if (isSlideCodeType && !state.dxToken) {
      isverifyTokenValid = "请滑动验证";
    }
    if (!isSlideCodeType) {
      isImageCodeValid = validateImageCode(state.imageCode);
    }

    if (
      isUsernameValid ||
      isImageCodeValid ||
      isPasswordValid ||
      isConfirmPasswordValid || isverifyTokenValid
    ) {
      return Toast.show({
        content: isUsernameValid ||
          isImageCodeValid ||
          isPasswordValid ||
          isConfirmPasswordValid||isverifyTokenValid,
      });
    }

    doRegister();
  };

  const phoneNumberRegister = () => {
    const isPhoneNumberValid = validatePhoneNumber(state.phoneNumber);
    const isSMSCodeValid = validateSMSCode(state.smsCode);
    const isPasswordValid = validatePassword(state.password);
    const isConfirmPasswordValid = validateConfirmPassword(
      state.confirmPassword,
      state.password,
    );

    let isImageCodeValid = undefined;
    let isverifyTokenValid = undefined;
    if (isSlideCodeType && !state.dxToken) {
      isverifyTokenValid = "请滑动验证";
    }
    if (!isSlideCodeType) {
      isImageCodeValid = validateImageCode(state.imageCode);
    }

    if (
      isPhoneNumberValid ||
      isImageCodeValid ||
      isSMSCodeValid ||
      isPasswordValid ||
      isConfirmPasswordValid || isverifyTokenValid
    ) {
      return Toast.show({
        content: isPhoneNumberValid ||
          isImageCodeValid ||
          isSMSCodeValid ||
          isPasswordValid ||
          isConfirmPasswordValid || isverifyTokenValid,
      });
    }
    doRegister();
  };

  const submit = () => {
    // TODO：顶象验证和图片二选一
    if (isUserNameRegisterMode) usernameRegister();
    if (isPhoneNumberRegisterMode) phoneNumberRegister();
  };

  useEffect(() => {
    flushCodeImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    let dx_token = document.getElementById('dx_token');
    if(dx_token){
        insertDx((_dx) => {
          const dx_obj = _dx.Captcha(document.getElementById('dx_token'), {
            appId: process.env.REACT_APP_DX_APPID, //appId，在控制台应用管理或应用配置模块获取
            apiServer: process.env.REACT_APP_DX_API_SERVER,
            style: "inline",
            success: (token:string) => {
              // 获取验证码token，用于后端校验，注意获取token若是sl开头的字符串，则是前端网络不通生成的降级token,请检查前端网络及apiServer地址。
              console.log('dxToken==',token);
              dispatch({
                type: "set_dx_token",
                dxToken: token,
              });
            }
          });
          setDxObj(dx_obj);
        });
    }
  }, []);

  const isSlideCodeType = config?.verifyCodeType === "2";

  return {
    loading,
    state,
    dispatch,
    submit,
    flushSMSCode,
    flushCodeImage,
    isUserNameRegisterMode,
    isPhoneNumberRegisterMode,
    announce,
    isSlideCodeType,
  };
};
